import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * SLIDE-H: Horizontal sliding window (schuifraam horizontaal).
 * One panel slides left-right on a track.
 */
export class SlideHSection implements SectionInterface {
  readonly group: THREE.Group;
  private slideGroup: THREE.Group;
  private maxSlide: number;
  private slideBaseX: number;

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-slideh-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: 'SLIDE-H' };

    // Effective dimensions for the sliding sash (inset from mullions)
    const effW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const panelW = effW / 2;
    const clearW = panelW - 2 * PROFILE.sashWidth;
    const clearH = effH - 2 * PROFILE.sashWidth;
    this.maxSlide = panelW;

    const glassMat = materialLib.glass(config.infill.tint, config.infill.glazingTier);

    // Each panel gets half the sash depth so together they span the same
    // total depth as a single sash on any other section type.
    // Fixed panel sits on the back track, sliding panel on the front track.
    const halfDepth = PROFILE.sashDepth / 2;

    // Fixed panel (left half) — back track
    const fixedSash = createSash(panelW, effH, PROFILE.sashWidth, halfDepth, frameMaterial);
    fixedSash.position.set(insets.left, insets.bottom, -halfDepth / 2);
    this.group.add(fixedSash);

    const fixedGlass = createGlassPane(clearW, clearH, PROFILE.glassThickness, glassMat);
    fixedGlass.position.set(insets.left + panelW / 2, insets.bottom + effH / 2, -halfDepth / 2);
    this.group.add(fixedGlass);

    // Sliding panel (right half) — front track, moves left-right
    this.slideGroup = new THREE.Group();
    this.slideGroup.name = 'slide-panel';

    const slideSash = createSash(panelW, effH, PROFILE.sashWidth, halfDepth, frameMaterial);
    this.slideGroup.add(slideSash);

    const slideGlass = createGlassPane(clearW, clearH, PROFILE.glassThickness, glassMat);
    slideGlass.position.set(panelW / 2, effH / 2, 0);
    this.slideGroup.add(slideGlass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(panelW / 2, effH / 2, 0);
      this.slideGroup.add(roeden);
    }

    const handle = createHandle(materialLib.handleMaterial(config.hardware.handleFinish));
    handle.position.set(PROFILE.sashWidth / 2, effH / 2, halfDepth / 2);
    this.slideGroup.add(handle);

    this.slideBaseX = insets.left + panelW;
    this.slideGroup.position.set(this.slideBaseX, insets.bottom, halfDepth / 2);
    this.group.add(this.slideGroup);

    if (config.openAmount > 0) {
      this.setTranslation(config.openAmount * this.maxSlide);
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setTranslation(t: number): void {
    this.slideGroup.position.x = this.slideBaseX - t;
  }

  setOpenAngle(rad: number): void {
    this.setTranslation(rad / (Math.PI / 2) * this.maxSlide);
  }
}
